import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ActuarialPVCalculator() {
  const { t } = useTranslation();
  const [benefit, setBenefit] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [survivalProb, setSurvivalProb] = useState("");
  const [years, setYears] = useState("");
  const [result, setResult] = useState<{ apv: number; totalProbability: number } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: 'https://calclab.app' },
      { name: t('categories.finance'), url: 'https://calclab.app/finance' },
      { name: "Actuarial PV Calculator", url: "https://calclab.app/calculator/actuarial-pv" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Actuarial Present Value Calculator",
      "Calculate present value of insurance benefits and annuities with mortality. Enter benefit, interest rate, survival probability, and years.",
      "https://calclab.app/calculator/actuarial-pv",
      lang
    )
  ]);

  const calculate = () => {
    const b = parseFloat(benefit);
    const r = parseFloat(interestRate) / 100;
    const p = parseFloat(survivalProb) / 100;
    const n = parseFloat(years);

    if (isNaN(b) || isNaN(r) || isNaN(p) || isNaN(n) ||
        b <= 0 || n <= 0 || p < 0 || p > 1) {
      toast.error("Please enter valid values (probability must be between 0-100%)");
      return;
    }

    // APV = Σ [Benefit × Survival Probability × Discount Factor]
    let apv = 0;
    let cumulativeSurvival = 1;

    for (let t = 1; t <= n; t++) {
      cumulativeSurvival *= p; // Probability of surviving to year t
      const discountFactor = 1 / Math.pow(1 + r, t);
      apv += b * cumulativeSurvival * discountFactor;
    }

    setResult({ 
      apv, 
      totalProbability: cumulativeSurvival 
    });
    toast.success("Actuarial Present Value calculated!");
  };

  const reset = () => {
    setBenefit("");
    setInterestRate("");
    setSurvivalProb("");
    setYears("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Actuarial Present Value Calculator</CardTitle>
          <CardDescription>
            Calculate present value of insurance benefits and annuities with mortality
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="benefit">Annual Benefit ($)</Label>
            <Input
              id="benefit"
              type="number"
              placeholder="10000"
              value={benefit}
              onChange={(e) => setBenefit(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Interest Rate (%)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="5"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="survival">Annual Survival Probability (%)</Label>
            <Input
              id="survival"
              type="number"
              placeholder="98"
              value={survivalProb}
              onChange={(e) => setSurvivalProb(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Probability of surviving each year (e.g., 98% means 2% mortality rate)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="years">Number of Years</Label>
            <Input
              id="years"
              type="number"
              placeholder="20"
              value={years}
              onChange={(e) => setYears(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>Actuarial PV:</strong> ${result.apv.toFixed(2)}
                  </p>
                  <CopyResultButton value={result.apv.toFixed(2)} />
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm">
                  <strong>Survival to End:</strong> {(result.totalProbability * 100).toFixed(2)}%
                </p>
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["actuarial-pv"]} trigger={result?.apv} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
